import { Grid } from '@mui/material';
import { Checkbox, HSpacer, RoutalDialog, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { Project } from '../../../../../domain/backoffice/project/Project';
import { useUpdateProjectManagedVehicles } from '../../../../../hooks/backoffice/projects/useUpdateProjectManagedVehicles';
import Button from '../../../../../react-components/Button';
import InputText from '../../../../../react-components/InputText';
import Text from '../../../../../react-components/Text';

type BackofficeClientsProjectsManageVehiclesProps = {
  openDialog: boolean;
  project: Project;
  onClose: (hasBeenUpdated: boolean) => void;
};

export const BackofficeClientsProjectsManageVehiclesDialog = ({
  openDialog,
  project,
  onClose,
}: BackofficeClientsProjectsManageVehiclesProps) => {
  const { run: updateManagedVehicles, isLoading, success } = useUpdateProjectManagedVehicles();
  const [confirmState, setConfirmState] = useState<boolean>(false);
  const [prorate, setProrate] = useState<boolean>(true);
  const [managed, setManaged] = useState<boolean>(project.managed ?? false);
  const [managedVehicles, setManagedVehicles] = useState<number>(project.resources.max_vehicles_plan ?? 0);

  useEffect(() => {
    if (success) {
      onClose(true);
    }
  }, [onClose, success]);

  if (!openDialog) return null;

  const isSaveButtonEnabled = () => {
    if (isLoading) {
      return false;
    }

    if (project.managed !== managed) {
      return true;
    }

    if (managed && project.resources.max_vehicles_plan !== managedVehicles) {
      return true;
    }

    return false;
  };

  return (
    <RoutalDialog
      open={openDialog}
      onCloseDialog={() => {
        if (!isLoading) {
          onClose(false);
        }
      }}
      dialogProps={{
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
      }}
      titleContent={<Text variant="h6" intlId="backoffice.clients.projects.manage_vehicles" />}
      footerContent={
        !confirmState ? (
          <Grid container justifyContent="flex-end">
            <Button disabled={isLoading} intlId="cancel" variant="flat" onClick={() => onClose(false)} />

            <HSpacer small />

            <Button
              intlId="save"
              disabled={!isSaveButtonEnabled()}
              onClick={() => {
                setConfirmState(true);
              }}
            />
          </Grid>
        ) : (
          <Grid container direction="row" alignItems="center" justifyContent="space-between" wrap="nowrap">
            <Grid item xs={5} container direction="row" justifyContent="flex-start">
              <Button disabled={isLoading} intlId="cancel" variant="flat" onClick={() => setConfirmState(false)} />

              <HSpacer small />

              <Button
                disabled={!isSaveButtonEnabled()}
                onClick={() => {
                  updateManagedVehicles({
                    projectId: project.id,
                    managedVehicles: managed ? managedVehicles : null,
                    prorate,
                  });
                }}
              >
                {isLoading ? (
                  <Text style={{ color: `white` }} intlId="loading" />
                ) : (
                  <Text
                    style={{ cursor: isSaveButtonEnabled() ? `pointer` : `default`, color: `white` }}
                    intlId="confirm"
                  />
                )}
              </Button>
            </Grid>

            <Grid item xs={7} container direction="row" justifyContent="flex-end">
              <Text intlId="backoffice.clients.projects.manage_vehicles.confirm_text" />
            </Grid>
          </Grid>
        )
      }
    >
      <Grid container direction="column" wrap="nowrap">
        <Grid item container direction="row" wrap="nowrap" alignItems="center">
          <Grid item>
            <Checkbox
              checked={managed}
              onChange={(e: any, value: any) => {
                setManaged(value);
              }}
              disabled={isLoading}
            />
          </Grid>

          <HSpacer small />

          <Grid item>
            <Text tagName="span" onClick={() => setManaged(!managed)} intlId="backoffice.clients.projects.managed" />
          </Grid>
        </Grid>

        {managed ? (
          <>
            <VSpacer large />

            <Grid item container direction="column" wrap="nowrap" alignItems="flex-start">
              <Text tagName="span" variant="label" intlId="backoffice.clients.projects.managed_vehicles" />

              <VSpacer small />

              <InputText
                id="managed_vehicles"
                type="number"
                disabled={isLoading}
                fieldValue={managedVehicles}
                required
                onChange={(value: any) => {
                  setManagedVehicles(value);
                }}
              />
            </Grid>
          </>
        ) : null}

        <VSpacer large />

        <Grid container alignItems="center">
          <Grid item>
            <Checkbox checked={prorate} onChange={(e: any, value: any) => setProrate(value)} />
          </Grid>

          <HSpacer small />

          <Grid item>
            <Text
              tagName="span"
              onClick={() => setProrate(!prorate)}
              intlId="backoffice.clients.projects.managed_vehicles.prorate_changes"
            />
          </Grid>
        </Grid>
      </Grid>
    </RoutalDialog>
  );
};
